import { BIP39_ENGLISH } from './bip39-english.js';

const WORD_INDEX = new Map(BIP39_ENGLISH.map((word, index) => [word, index]));
const EXPORT_PAYLOAD_LENGTH = 64;

function bytesToBinary(bytes) {
  return Array.from(bytes, (byte) => byte.toString(2).padStart(8, '0')).join('');
}

function binaryToBytes(binary) {
  const bytes = new Uint8Array(binary.length / 8);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = parseInt(binary.slice(index * 8, (index + 1) * 8), 2);
  }
  return bytes;
}

export function normalizeMnemonicPhrase(phrase) {
  return String(phrase || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

export async function encodeMnemonicPhrase(payload) {
  if (!(payload instanceof Uint8Array) || payload.length !== EXPORT_PAYLOAD_LENGTH) {
    throw new Error('Invalid identity export payload');
  }

  const entropyBits = payload.length * 8;
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', payload));
  const checksumBits = bytesToBinary(digest).slice(0, entropyBits / 32);
  const bits = `${bytesToBinary(payload)}${checksumBits}`;
  const words = [];

  for (let index = 0; index < bits.length; index += 11) {
    const wordIndex = parseInt(bits.slice(index, index + 11), 2);
    const word = BIP39_ENGLISH[wordIndex];
    if (!word) {
      throw new Error('Failed to encode identity mnemonic');
    }
    words.push(word);
  }

  return words;
}

export async function decodeMnemonicPhrase(words) {
  const normalized = normalizeMnemonicPhrase(words.join(' '));
  if (normalized.length !== 48) {
    throw new Error('Recovery phrase must contain exactly 48 words');
  }

  let bits = '';
  for (const word of normalized) {
    const wordIndex = WORD_INDEX.get(word);
    if (wordIndex === undefined) {
      throw new Error(`Unknown recovery word: ${word}`);
    }
    bits += wordIndex.toString(2).padStart(11, '0');
  }

  const entropyBits = (normalized.length * 11) - (normalized.length * 11 / 33);
  const checksumBits = entropyBits / 32;
  const entropyBinary = bits.slice(0, entropyBits);
  const checksumBinary = bits.slice(entropyBits, entropyBits + checksumBits);
  const payload = binaryToBytes(entropyBinary);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', payload));
  const expectedChecksum = bytesToBinary(digest).slice(0, checksumBits);

  if (checksumBinary !== expectedChecksum) {
    throw new Error('Invalid recovery phrase checksum');
  }

  if (payload.length !== EXPORT_PAYLOAD_LENGTH) {
    throw new Error('Invalid recovery phrase payload');
  }

  return payload;
}
